import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import CtaBand from '../components/CtaBand';
import CountUp from '../components/CountUp';
import { company, legalLinks, products, stats, values } from '../data/site';
import './pages.css';

const About: React.FC = () => (
    <>
        <PageHero
            kicker="About us"
            title={
                <>
                    A technology company with a <span className="grad-text">retail backbone</span>
                </>
            }
            description={`${company.legalName} started in ${company.founded} in Bagodar, Jharkhand. What began as a retail business is now a software company that builds, hosts and supports the platforms other organizations run on — while continuing to operate its own SuperMart network.`}
        />

        <section className="section section--light">
            <div className="container page-split page-split--wide">
                <div data-reveal="left">
                    <span className="kicker">Our story</span>
                    <h2>Built from running a real business</h2>
                    <p className="lede">
                        We did not start by writing software for other people. We started by
                        running stores — dealing with billing queues, stock counts, staff
                        attendance and salary days ourselves.
                    </p>
                    <p className="lede">
                        The tools available were either too expensive, too complicated, or built
                        for a market that works nothing like ours. So we built our own. Those
                        internal tools became Feeisy, PAGARisy, Promoisy, QUOTATIONisy and Fixisy —
                        now used by schools, businesses and service centres well beyond our own
                        operations.
                    </p>
                    <p className="lede">
                        That origin still shapes the products. Every workflow in them exists
                        because somebody had to do that job on a busy day.
                    </p>
                </div>

                <div className="mission-stack">
                    <article className="mission-card" data-reveal="right" style={{ ['--i' as string]: 0 }}>
                        <h3>Our mission</h3>
                        <p>
                            Make dependable operational software affordable for organizations that
                            larger vendors overlook — schools, small businesses and service
                            operations across India.
                        </p>
                    </article>
                    <article className="mission-card" data-reveal="right" style={{ ['--i' as string]: 1 }}>
                        <h3>Our vision</h3>
                        <p>
                            A connected suite where an organization can run admissions, fees,
                            payroll, quotations, service jobs and customer communication on one
                            trusted platform.
                        </p>
                    </article>
                    <article className="mission-card" data-reveal="right" style={{ ['--i' as string]: 2 }}>
                        <h3>Our commitment</h3>
                        <p>
                            We build, host and support in-house. When something breaks, there is no
                            third party to blame and no ticket queue to disappear into.
                        </p>
                    </article>
                </div>
            </div>
        </section>

        <section className="section section--tight section--alt">
            <div className="container">
                <div className="grid grid--4">
                    {stats.map((stat, i) => (
                        <div
                            className="stat"
                            key={stat.label}
                            data-reveal="up"
                            style={{ ['--i' as string]: i }}
                        >
                            <strong className="grad-text">
                                <CountUp value={stat.value} />
                            </strong>
                            <span>{stat.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        <section className="section section--light">
            <div className="container">
                <div className="sec-head sec-head--center" data-reveal="up">
                    <span className="kicker">How we work</span>
                    <h2>Four things we do not compromise on</h2>
                </div>
                <div className="grid grid--4">
                    {values.map((value, i) => (
                        <article
                            className="card service-card"
                            key={value.title}
                            data-reveal="up"
                            style={{ ['--i' as string]: i }}
                        >
                            <span className="service-card__icon" aria-hidden="true">
                                {value.icon}
                            </span>
                            <h3>{value.title}</h3>
                            <p>{value.description}</p>
                        </article>
                    ))}
                </div>
            </div>
        </section>

        <section className="section section--dark">
            <div className="container page-split">
                <div data-reveal="left">
                    <span className="kicker">Company information</span>
                    <h2>Registered details &amp; compliance</h2>
                    <p className="lede">
                        Full statutory details, published for customers, partners and platform
                        reviewers.
                    </p>
                    <div className="chip-row about-legal">
                        {legalLinks.map((link) => (
                            <a
                                key={link.href}
                                className="chip chip--dark"
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {link.label}
                            </a>
                        ))}
                    </div>
                </div>

                <dl className="info-table" data-reveal="right">
                    <div>
                        <dt>Legal name</dt>
                        <dd>{company.legalName}</dd>
                    </div>
                    <div>
                        <dt>Registered office</dt>
                        <dd>
                            {company.address.line1}, {company.address.line2}, {company.address.country}
                        </dd>
                    </div>
                    <div>
                        <dt>GSTIN</dt>
                        <dd className="mono">{company.gst}</dd>
                    </div>
                    <div>
                        <dt>CIN</dt>
                        <dd className="mono">{company.cin}</dd>
                    </div>
                    <div>
                        <dt>Email</dt>
                        <dd>
                            <a href={`mailto:${company.email}`}>{company.email}</a>
                        </dd>
                    </div>
                    <div>
                        <dt>Phone</dt>
                        <dd>
                            <a href={`tel:+${company.phoneRaw}`}>{company.phone}</a>
                        </dd>
                    </div>
                    <div>
                        <dt>Platforms</dt>
                        <dd>{products.map((p) => p.name).join(', ')}</dd>
                    </div>
                </dl>
            </div>
        </section>

        <section className="section section--tight section--alt">
            <div className="container about-divisions">
                <article data-reveal="up" style={{ ['--i' as string]: 0 }}>
                    <h3>Technology division</h3>
                    <p>
                        SaaS products, custom software, integrations, cloud hosting and support for
                        schools, businesses and service organizations.
                    </p>
                    <Link className="link-arrow" to="/services">
                        IT services <span aria-hidden="true">→</span>
                    </Link>
                </article>
                <article data-reveal="up" style={{ ['--i' as string]: 1 }}>
                    <h3>Retail division</h3>
                    <p>
                        Kifayti SuperMart stores offering groceries, fashion, electronics and home
                        essentials — now expanding through franchise partners.
                    </p>
                    <Link className="link-arrow" to="/franchise">
                        Franchise opportunities <span aria-hidden="true">→</span>
                    </Link>
                </article>
            </div>
        </section>

        <CtaBand
            title="Work with us"
            description="Whether you want to digitalize an organization, commission a custom platform, or open a Kifayti mall — start with a conversation."
        />
    </>
);

export default About;
